<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_self_test_generic_assessment') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <div class="kds-typography-paragraph-large">
          {{
            $t('b2b__self_test__phq__description', {
              steps: question.step,
            })
          }}
        </div>
        <h3 class="kds-typography-title-xlarge">
          {{ $t(question.headerKey) }}
        </h3>
        <div
          class="kds-typography-paragraph-large"
          v-html="$t('b2b__self_test__physical_activity__msk_us__items_hint')"
        />
      </div>
      <div class="self-test">
        <fieldset>
          <div
            v-for="item in items"
            :key="item.key"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': item.value === phqSelectedValue },
            ]"
            @click="handleRadioButtonChange(item)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t(item.translation) }}
            </label>
            <div class="quiz-radio__layout">
              <custom-radio
                :checked="item.value === phqSelectedValue"
                :data-q-a-value="item.key"
                :value="item.key"
                data-q-a="phq-selected-value"
              />
            </div>
          </div>
        </fieldset>
      </div>
    </div>
    <template #actions>
      <BaseButton
        :disabled="!isValid"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="handleCTAButtonEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import useSelfTestReevaluationStore from '@shared/modules/assessment/stores/useSelfTestReevaluationStore.js'

import phqOptions from '@shared/modules/assessment/config/phq-options.json'
import ROUTE from '@shared/modules/assessment/router/names'

import CustomRadio from '@shared/components/CustomRadio.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const route = useRoute()
const reevaluationsStore = useSelfTestReevaluationStore()

const question = computed(() => {
  switch (route.name) {
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_PHQ_INTEREST:
      return {
        headerKey: 'b2b__self_test__phq_interest__header',
        step: '1/9',
        getter: () => reevaluationsStore.phqInterest,
        setter: (value) => (reevaluationsStore.phqInterest = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_PHQ_FEELING:
      return {
        headerKey: 'b2b__self_test__phq_feeling__header',
        step: '2/9',
        getter: () => reevaluationsStore.phqFeeling,
        setter: (value) => (reevaluationsStore.phqFeeling = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_NERVOUS:
      return {
        headerKey: 'b2b__self_test__gad_nervous__header',
        step: '3/9',
        getter: () => reevaluationsStore.gadNervous,
        setter: (value) => (reevaluationsStore.gadNervous = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_CONTROL:
      return {
        headerKey: 'b2b__self_test__gad_control__header',
        step: '4/9',
        getter: () => reevaluationsStore.gadControl,
        setter: (value) => (reevaluationsStore.gadControl = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_WORRYING:
      return {
        headerKey: 'b2b__self_test__gad_worrying__header',
        step: '5/9',
        getter: () => reevaluationsStore.gadWorrying,
        setter: (value) => (reevaluationsStore.gadWorrying = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_RELAXING:
      return {
        headerKey: 'b2b__self_test__gad_relaxing__header',
        step: '6/9',
        getter: () => reevaluationsStore.gadRelaxing,
        setter: (value) => (reevaluationsStore.gadRelaxing = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_RESTLESS:
      return {
        headerKey: 'b2b__self_test__gad_restless__header',
        step: '7/9',
        getter: () => reevaluationsStore.gadRestless,
        setter: (value) => (reevaluationsStore.gadRestless = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_IRRITABLE:
      return {
        headerKey: 'b2b__self_test__gad_irritable__header',
        step: '8/9',
        getter: () => reevaluationsStore.gadIrritable,
        setter: (value) => (reevaluationsStore.gadIrritable = value),
      }
    case ROUTE.ASSESSMENT_SELF_TEST_REEVALUATIONS_GAD_AFRAID:
      return {
        headerKey: 'b2b__self_test__gad_afraid__header',
        step: '9/9',
        getter: () => reevaluationsStore.gadAfraid,
        setter: (value) => (reevaluationsStore.gadAfraid = value),
      }
    default:
      return null
  }
})

const phqSelectedValue = computed(() => question.value.getter())

const items = ref(phqOptions.concat([]))

const handleRadioButtonChange = (item) => {
  question.value.setter(item.value)
}

const handleCTAButtonEvent = (event) => {
  props.handleNextEvent(event)
}

const isValid = computed(() => phqSelectedValue.value > -1)
</script>

<style lang="scss">
@use '@shared/modules/assessment/views/css/SelfTest.scss' as *;
</style>
